import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  getDirectory, getDirectoryItem, createDirectoryItem, updateDirectoryItem, deleteDirectoryItem, listDirectory, errorText,
} from './apiDirectory';
import {
  COLLECTIONS, PRICE_RANGES, STATUSES, buildPayload, collectionLabel, singularLabel, featureOn, termOptions,
  hoursToDays, daysToHours, defaultDays, attributesToRows, rowsToAttributes, isSlug, slugify, publicPath, nounsFrom,
} from './directoryHelpers';
import { AttributesEditor, ChipSelect, HoursEditor, ListingPicker, MediaField } from './fields';
import Field, { CheckField } from '../editor/Field';
import { useDomain } from '../DomainContext';
import { useToast } from '../Toast';
import PageHeader from '../ui/PageHeader';
import Alert from '../ui/Alert';
import ThinBanner from '../seo/ThinBanner';
import Spinner from '../ui/Spinner';
import ConfirmDialog from '../ui/ConfirmDialog';

const EMPTY = {
  listings: { name: '', slug: '', kind: '', services: [], photos: [], claimed: false, featured: false, status: 'active' },
  people: { name: '', slug: '', specialties: [], listing_slugs: [], portfolio: [], consent_at: null, status: 'active' },
  authors: { name: '', slug: '', status: 'active' },
};

const Section = ({ title, children }) => (
  <section className="card">
    <h3 className="card-title" style={{ marginTop: 0 }}>{title}</h3>
    {children}
  </section>
);

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

export default function DirectoryEditor() {
  const { domainId, collection, slug: routeSlug } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { domains, selectedDomain } = useDomain();
  const isNew = routeSlug === 'new';
  const valid = COLLECTIONS.includes(collection);

  const [vocab, setVocab] = useState(null);
  const [original, setOriginal] = useState({});
  const [draft, setDraft] = useState(EMPTY[collection] || {});
  const [days, setDays] = useState(null); // null = hours not listed
  const [attrRows, setAttrRows] = useState([]);
  const [slugTouched, setSlugTouched] = useState(false);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [hourErrors, setHourErrors] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(false);

  const domain = selectedDomain?.id === domainId ? selectedDomain : domains.find((d) => d.id === domainId) || null;
  const listPath = `/domain/${encodeURIComponent(domainId)}/directory?tab=${collection}`;
  const nouns = nounsFrom(vocab);
  const label = singularLabel(vocab, collection);

  useEffect(() => {
    if (!valid) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [cfg, item, peopleList] = await Promise.all([
          getDirectory(domainId),
          isNew ? Promise.resolve(null) : getDirectoryItem(domainId, collection, routeSlug),
          collection === 'authors' ? listDirectory(domainId, 'people', { limit: 200 }) : Promise.resolve(null),
        ]);
        if (cancelled) return;
        setVocab(cfg.vocab || null);
        if (peopleList) setPeople(peopleList.items || []);
        if (item) {
          setOriginal(item);
          setDraft(item);
          setDays(item.hours ? hoursToDays(item.hours) : null);
          setAttrRows(attributesToRows(item.attributes));
        }
      } catch (err) {
        if (cancelled) return;
        if (err.response?.status === 401) navigate('/login');
        else setError(errorText(err, `Failed to load this ${collection === 'authors' ? 'author' : 'item'}`));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [domainId, collection, routeSlug, isNew, valid, navigate]);

  const set = (field, value) => {
    setDraft((d) => {
      const next = { ...d, [field]: value };
      if (field === 'name' && isNew && !slugTouched) {
        next.slug = slugify(collection === 'listings' && d.city ? `${value} ${d.city}` : value);
      }
      if (field === 'city' && isNew && !slugTouched && collection === 'listings') {
        next.slug = slugify(`${d.name || ''} ${value}`);
      }
      return next;
    });
    setFieldErrors((e) => ({ ...e, [field]: undefined }));
  };
  const text = (field) => ({ value: draft[field] ?? '', onChange: (e) => set(field, e.target.value) });

  const kindOptions = useMemo(() => termOptions(vocab, 'kinds'), [vocab]);
  const serviceOptions = useMemo(() => termOptions(vocab, 'services'), [vocab]);

  if (!valid) {
    return <div className="page"><Alert type="error">Unknown directory section “{collection}”.</Alert></div>;
  }
  if (loading) {
    return <div className="page page-narrow"><Spinner label="Loading…" /></div>;
  }

  const validate = () => {
    const errs = {};
    if (!(draft.name || '').trim()) errs.name = 'Name is required';
    if (isNew && !isSlug(draft.slug)) errs.slug = 'Slug must use a-z, 0-9 and -';
    if (collection === 'people' && !draft.consent_at) errs.consent_at = `A ${nouns.person} must consent before being listed`;
    return errs;
  };

  const handleSave = async (e) => {
    e?.preventDefault();
    const errs = validate();
    const payloadDraft = { ...draft };
    if (collection === 'listings') {
      if (featureOn(vocab, 'hours')) {
        const { hours, errors } = days ? daysToHours(days) : { hours: null, errors: {} };
        setHourErrors(errors);
        if (Object.keys(errors).length) errs.hours = 'Fix the hours';
        payloadDraft.hours = hours;
      } else {
        delete payloadDraft.hours;
      }
      const { attributes, error: attrErr } = rowsToAttributes(attrRows);
      if (attrErr) errs.attributes = attrErr;
      payloadDraft.attributes = attributes || {};
      if (!featureOn(vocab, 'booking')) delete payloadDraft.booking_url;
      if (!featureOn(vocab, 'price_range')) delete payloadDraft.price_range;
    }
    if (collection === 'people' && !featureOn(vocab, 'booking')) delete payloadDraft.booking_url;
    setFieldErrors(errs);
    if (Object.keys(errs).length) {
      setError('Fix the highlighted fields before saving.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const body = buildPayload(collection, payloadDraft, { original, isNew });
      if (isNew) {
        const created = await createDirectoryItem(domainId, collection, body);
        addToast(`${label} created`, 'success');
        navigate(`/domain/${encodeURIComponent(domainId)}/directory/${collection}/${encodeURIComponent(created.slug)}/edit`, { replace: true });
      } else {
        const updated = await updateDirectoryItem(domainId, collection, routeSlug, body);
        setOriginal(updated);
        setDraft(updated);
        setDays(updated.hours ? hoursToDays(updated.hours) : null);
        setAttrRows(attributesToRows(updated.attributes));
        addToast('Saved', 'success');
      }
    } catch (err) {
      if (err.response?.status === 401) navigate('/login');
      else setError(errorText(err, 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setConfirmDelete(false);
    try {
      await deleteDirectoryItem(domainId, collection, routeSlug);
      addToast(`${label} deleted`, 'success');
      navigate(listPath);
    } catch (err) {
      setError(errorText(err, 'Failed to delete'));
    }
  };

  const slugForMedia = isNew ? (isSlug(draft.slug) ? draft.slug : '') : routeSlug;
  const host = domain?.hostname || domain?.name;
  const livePath = !isNew ? publicPath(vocab, collection, routeSlug) : null;
  const mediaError = (msg) => setError(msg);

  const commonTop = (
    <Section title="Basics">
      <Field id="dir-name" label="Name" required error={fieldErrors.name}>
        <input id="dir-name" className={`input${fieldErrors.name ? ' is-invalid' : ''}`} {...text('name')} />
      </Field>
      <Field id="dir-slug" label="Slug" required={isNew} error={fieldErrors.slug}
             hint={isNew ? 'The URL segment; cannot be changed later.' : 'The slug cannot be changed.'}>
        <input id="dir-slug" className="input mono" value={isNew ? (draft.slug || '') : routeSlug} disabled={!isNew}
               onChange={(e) => { setSlugTouched(true); set('slug', e.target.value); }} />
      </Field>
      {collection !== 'listings' && (
        <Field id="dir-title" label="Title" hint={collection === 'authors' ? 'e.g. "Licensed cosmetologist"' : undefined}>
          <input id="dir-title" className="input" {...text('title')} />
        </Field>
      )}
      <Field id="dir-status" label="Status" hint="Hidden keeps the document but takes it off the site.">
        <select id="dir-status" className="select" value={draft.status || 'active'} onChange={(e) => set('status', e.target.value)}>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </Field>
    </Section>
  );

  const contact = (fields) => (
    <Section title="Links & contact">
      <div className="field-row">
        {fields.includes('phone') && (
          <Field id="dir-phone" label="Phone" hint="10 digits">
            <input id="dir-phone" className="input" inputMode="tel" {...text('phone')} />
          </Field>
        )}
        {fields.includes('instagram') && (
          <Field id="dir-instagram" label="Instagram">
            <input id="dir-instagram" className="input" placeholder="handle or URL" {...text('instagram')} />
          </Field>
        )}
      </div>
      {fields.includes('website') && (
        <Field id="dir-website" label="Website">
          <input id="dir-website" className="input" type="url" placeholder="https://" {...text('website')} />
        </Field>
      )}
      {fields.includes('booking_url') && featureOn(vocab, 'booking') && (
        <Field id="dir-booking" label="Booking URL">
          <input id="dir-booking" className="input" type="url" placeholder="https://" {...text('booking_url')} />
        </Field>
      )}
    </Section>
  );

  let body;
  if (collection === 'listings') {
    body = (
      <>
        {commonTop}
        <Section title="Details">
          <Field id="dir-kind" label="Kind">
            <select id="dir-kind" className="select" value={draft.kind || ''} onChange={(e) => set('kind', e.target.value)}>
              <option value="">—</option>
              {kindOptions.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
              {draft.kind && !kindOptions.some((k) => k.key === draft.kind) && <option value={draft.kind}>{draft.kind} (not in vocab)</option>}
            </select>
          </Field>
          <Field id="dir-services" label={nouns.services.charAt(0).toUpperCase() + nouns.services.slice(1)}>
            <ChipSelect id="dir-services" options={serviceOptions} value={draft.services} onChange={(v) => set('services', v)}
                        emptyText="This domain's vocab defines no services." />
          </Field>
          <Field id="dir-description" label="Description" hint="Plain text or light markdown.">
            <textarea id="dir-description" className="textarea" rows={5} {...text('description')} />
          </Field>
          {featureOn(vocab, 'price_range') && (
            <Field id="dir-price" label="Price range">
              <select id="dir-price" className="select" value={draft.price_range || ''} onChange={(e) => set('price_range', e.target.value)}>
                <option value="">—</option>
                {PRICE_RANGES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
          )}
          <CheckField id="dir-claimed" label="Claimed — an owner verified control of this listing" checked={draft.claimed} onChange={(v) => set('claimed', v)} />
          <CheckField id="dir-featured" label="Featured (paid placement)" checked={draft.featured} onChange={(v) => set('featured', v)}
                      hint='Renders with a visible "Featured" label on the site.' />
        </Section>
        {featureOn(vocab, 'hours') && (
          <Section title="Hours">
            {days ? (
              <>
                <HoursEditor days={days} onChange={setDays} errors={hourErrors} />
                <button type="button" className="btn btn-ghost btn-danger-text btn-sm" style={{ marginTop: 8 }} onClick={() => setDays(null)}>
                  Clear hours (not listed)
                </button>
              </>
            ) : (
              <>
                <p className="muted" style={{ marginTop: 0 }}>Hours are not listed.</p>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setDays(defaultDays())}>Add hours</button>
              </>
            )}
          </Section>
        )}
        {contact(['phone', 'instagram', 'website', 'booking_url'])}
        <Section title="Photos">
          <MediaField domainId={domainId} slug={slugForMedia} value={draft.photos} multiple onChange={(v) => set('photos', v)} onError={mediaError} />
          <span className="hint">The first photo is the main one.</span>
        </Section>
        <Section title="Location">
          <Field id="dir-street" label="Street"><input id="dir-street" className="input" {...text('street')} /></Field>
          <div className="field-row">
            <Field id="dir-city" label="City"><input id="dir-city" className="input" {...text('city')} /></Field>
            <Field id="dir-city-slug" label="City slug" hint="Blank: derived from city on create.">
              <input id="dir-city-slug" className="input mono" {...text('city_slug')} />
            </Field>
          </div>
          <div className="field-row">
            <Field id="dir-state" label="State"><input id="dir-state" className="input" {...text('state')} /></Field>
            <Field id="dir-zip" label="ZIP"><input id="dir-zip" className="input" inputMode="numeric" {...text('zip')} /></Field>
            <Field id="dir-county" label="County"><input id="dir-county" className="input" {...text('county')} /></Field>
          </div>
          <div className="field-row">
            <Field id="dir-lat" label="Latitude"><input id="dir-lat" className="input" type="number" step="any" {...text('lat')} /></Field>
            <Field id="dir-lon" label="Longitude"><input id="dir-lon" className="input" type="number" step="any" {...text('lon')} /></Field>
          </div>
        </Section>
        <Section title="Attributes">
          <AttributesEditor rows={attrRows} onChange={setAttrRows} />
          {fieldErrors.attributes && <span className="error" role="alert">{fieldErrors.attributes}</span>}
        </Section>
        {featureOn(vocab, 'license') && original.license && (
          <Section title="License (read-only)">
            <dl style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '4px 16px', margin: 0, fontSize: 14 }}>
              {['authority', 'state', 'number', 'type', 'expires', 'verified_at'].map((k) => (
                <React.Fragment key={k}>
                  <dt className="muted">{k.replace('_', ' ')}</dt>
                  <dd style={{ margin: 0 }}>{original.license[k] || '—'}</dd>
                </React.Fragment>
              ))}
            </dl>
            <p className="hint" style={{ marginBottom: 0 }}>
              Refreshed by the ingest (scripts/directory). Sources: {(original.sources || []).join(', ') || '—'}
            </p>
          </Section>
        )}
      </>
    );
  } else if (collection === 'people') {
    body = (
      <>
        {commonTop}
        <Section title="Consent">
          <CheckField
            id="dir-consent"
            label={`${nouns.person.charAt(0).toUpperCase() + nouns.person.slice(1)} consented to be listed *`}
            checked={!!draft.consent_at}
            onChange={(v) => set('consent_at', v ? new Date().toISOString() : null)}
            hint={draft.consent_at
              ? `Recorded ${formatDate(draft.consent_at)}.${original.consent_at ? ' To withdraw, hide or delete this profile.' : ''}`
              : 'Required. Profiles are opt-in only: never create one from license data.'}
          />
          {fieldErrors.consent_at && <span className="error" role="alert">{fieldErrors.consent_at}</span>}
        </Section>
        <Section title="Profile">
          <Field id="dir-bio" label="Bio"><textarea id="dir-bio" className="textarea" rows={5} {...text('bio')} /></Field>
          <Field id="dir-specialties" label="Specialties">
            <ChipSelect id="dir-specialties" options={serviceOptions} value={draft.specialties} onChange={(v) => set('specialties', v)}
                        emptyText="This domain's vocab defines no services." />
          </Field>
          <Field id="dir-listings" label={`${collectionLabel(vocab, 'listings')} they work at`} hint="The first is their primary.">
            <ListingPicker domainId={domainId} value={draft.listing_slugs} onChange={(v) => set('listing_slugs', v)} noun={nouns.listings} />
          </Field>
          {featureOn(vocab, 'license') && (
            <Field id="dir-license" label="License number" hint="Shown only if they supplied it.">
              <input id="dir-license" className="input mono" {...text('license_number')} />
            </Field>
          )}
        </Section>
        {contact(['instagram', 'website', 'booking_url'])}
        <Section title="Photo & portfolio">
          <Field id="dir-photo" label="Photo">
            <MediaField domainId={domainId} slug={slugForMedia} value={draft.photo} onChange={(v) => set('photo', v)} onError={mediaError} />
          </Field>
          <Field id="dir-portfolio" label="Portfolio">
            <MediaField domainId={domainId} slug={slugForMedia} value={draft.portfolio} multiple onChange={(v) => set('portfolio', v)} onError={mediaError} />
          </Field>
        </Section>
      </>
    );
  } else {
    body = (
      <>
        {commonTop}
        <Section title="Profile">
          <Field id="dir-bio" label="Bio"><textarea id="dir-bio" className="textarea" rows={5} {...text('bio')} /></Field>
          <Field id="dir-credentials" label="Credentials" hint='e.g. "TX cosmetology operator license #…"'>
            <input id="dir-credentials" className="input" {...text('credentials')} />
          </Field>
          <Field id="dir-person" label={`Linked ${nouns.person}`}>
            <select id="dir-person" className="select" value={draft.person_slug || ''} onChange={(e) => set('person_slug', e.target.value)}>
              <option value="">— none —</option>
              {people.map((p) => <option key={p.slug} value={p.slug}>{p.name}</option>)}
              {draft.person_slug && !people.some((p) => p.slug === draft.person_slug) && <option value={draft.person_slug}>{draft.person_slug}</option>}
            </select>
          </Field>
          <Field id="dir-photo" label="Photo">
            <MediaField domainId={domainId} slug={slugForMedia} value={draft.photo} onChange={(v) => set('photo', v)} onError={mediaError} />
          </Field>
        </Section>
        {contact(['instagram', 'website'])}
      </>
    );
  }

  return (
    <div className="page page-narrow">
      <PageHeader
        title={isNew ? `New ${label.toLowerCase()}` : (original.name || routeSlug)}
        subtitle={isNew ? collectionLabel(vocab, collection) : (
          <>
            {label}
            {original.updated_at ? ` · updated ${formatDate(original.updated_at)}` : ''}
            {host && livePath && <> · <a href={`https://${host}${livePath}`} target="_blank" rel="noopener noreferrer">View on site ↗</a></>}
          </>
        )}
        actions={<Link to={listPath} className="btn btn-secondary">← Back to {collectionLabel(vocab, collection).toLowerCase()}</Link>}
      />
      <Alert type="error" onDismiss={() => setError(null)}>{error}</Alert>
      {collection === 'listings' && !isNew && <ThinBanner verdict={original?.thin} noun={label.toLowerCase()} />}
      <form className="stack" onSubmit={handleSave}>
        {body}
        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : isNew ? `Create ${label.toLowerCase()}` : 'Save changes'}</button>
          <Link to={listPath} className="btn btn-secondary">Cancel</Link>
          {!isNew && (
            <button type="button" className="btn btn-ghost btn-danger-text" style={{ marginLeft: 'auto' }} onClick={() => setConfirmDelete(true)}>
              Delete
            </button>
          )}
        </div>
      </form>
      <ConfirmDialog
        open={confirmDelete}
        title={`Delete this ${label.toLowerCase()}?`}
        message={collection === 'listings'
          ? 'The document is removed and the page 404s. A re-ingest recreates licensed listings; set status to hidden to keep one off the site instead.'
          : 'The document is removed and its page 404s. Set status to hidden to keep it without showing it.'}
        confirmLabel="Delete"
        danger
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}
